import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

interface InviteData {
  userId: string;
  inquiryId: string;
  brokerId: string;
  isInvite: boolean;
}

interface DealTerms {
  companyName: string;
  symbol: string;
  commitmentAmount: number;
  discountRate: number;
  vwapPercent: number;
  rapidPercent: number;
  termMonths: number;
  originationFee: number;
  brokerFirm: string;
}

const BrokerInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dealTerms, setDealTerms] = useState<DealTerms | null>(null);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      verifyInvite();
    }
  }, [token]);

  const verifyInvite = async () => {
    try {
      // Decode the token
      const decoded = jwtDecode<InviteData>(token!);
      setInviteData(decoded);

      // Fetch deal terms
      const response = await axios.get(`/api/broker/invite/verify/${token}`);
      setDealTerms(response.data.dealTerms);
      setLoading(false);
    } catch (error) {
      console.error('Error verifying invite:', error);
      setError('Invalid or expired invitation link');
      setLoading(false);
    }
  };

  const handleAccept = () => {
    // Store the invite token
    localStorage.setItem('brokerInviteToken', token!);
    // Navigate to questionnaire
    navigate('/questionnaire');
  };

  const handleReject = async () => {
    try {
      await axios.post(`/api/broker/invite/reject/${token}`);
      navigate('/');
    } catch (error) {
      console.error('Error rejecting invite:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !dealTerms) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600">{error || 'This invitation link is not valid'}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Equity Financing Proposal
          </h1>
          <p className="text-gray-600">
            {dealTerms.brokerFirm} has prepared a financing proposal for {dealTerms.companyName}
          </p>
        </motion.div>

        {/* Deal Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-6"
        >
          <h2 className="text-2xl font-semibold mb-6">Proposed Terms</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CurrencyDollarIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm text-gray-600">Total Commitment</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(dealTerms.commitmentAmount)}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm text-gray-600">Discount Rate</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {dealTerms.discountRate}%
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <ClockIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm text-gray-600">Term Length</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {dealTerms.termMonths} months
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircleIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm text-gray-600">Daily Purchase Limit</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {dealTerms.vwapPercent}% VWAP
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold mb-4">Key Features</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">
                  Non-dilutive equity financing - no warrants or equity kickers
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">
                  Flexible draw schedule - use funds as needed
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">
                  Daily volume limits protect stock price
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">
                  No financial covenants or restrictions
                </span>
              </li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
            <p className="text-sm text-yellow-800">
              <strong>Broker Fee:</strong> This transaction includes a {(dealTerms.originationFee * 100).toFixed(2)}% 
              origination fee payable to {dealTerms.brokerFirm} upon each closing.
            </p>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex gap-4"
        >
          <button
            onClick={handleReject}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Decline Proposal
          </button>
          <button
            onClick={handleAccept}
            className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
          >
            <CheckCircleIcon className="h-5 w-5" />
            Proceed to Create Account
          </button>
        </motion.div>

        <p className="text-center text-sm text-gray-500 mt-6">
          By proceeding, you agree to create an account and complete our qualification process
        </p>
      </div>
    </div>
  );
};

export default BrokerInvite; 